import React, { useEffect, useState } from "react";

// Hooks/APIs/Utils
import { useDispatch } from "react-redux";
import { getTranslateByKey } from "@/main/services/utils";
import { ManufacturerRequest } from "@/main/models/request";
import { getManufacturers, resetReducerManufacturers, useManufacturerReducer } from "@/main/store/vehicle/manufacturer";
import {
	CompanyManufacturerRequest,
	CompanyManufacturerSaveRequest,
} from "@/main/models/request/companyManufacturerRequest";
import { CompanyManufacturerResponse } from "@/main/models/response/companyManufacturerResponse";
import { companyManufacturerApi } from "@/main/api";
import { toast } from "react-toastify";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";

// Components
import { KeyValue } from "@/main/models/common";
import KOGAutoCompleteCheckbox from "../inputAutoCompleteCheck";
import SecondaryButton from "../button/secondaryButton";
import KOGPaginationGrid from "../paginationGrid/paginationGrid";
import BoxGrid from "../boxGrid/boxGrid";
import RowCompanyManufacturer from "./rowCompanyManufacturer";

// Styles
import * as SP from "@/main/components/shared/styles";
import * as S from "./styles";
import KogInputTextFilter from "../inputText/inputTextFilter";

interface ICompanyManufacturer {
	guidCompany: string | null | undefined;
	watcherCountry: KeyValue | null | undefined;
}

function CompanyManufacturer({ guidCompany }: ICompanyManufacturer) {
	// #region states
	const [dataManufacturers, setDataManufacturers] = useState<KeyValue[]>([]);
	const [isLoadingAddManufacturers, setIsLoadingAddManufacturers] = useState<boolean>(false);
	const [formFilters, setFormFilters] = useState<CompanyManufacturerRequest | null>(null);
	const [dataGrid, setDataGrid] = useState<CompanyManufacturerResponse[]>([]);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
	// #endregion

	// #region reducers
	const { manufacturers: manufacturerReducer } = useManufacturerReducer();
	// #endregion

	// #region hooks
	const dispatch = useDispatch();
	// #endregion

	// #region functions
	function callManufacturerApiDomain() {
		dispatch(resetReducerManufacturers());

		const request: ManufacturerRequest = {};

		dispatch(getManufacturers(request));
	}

	async function fetchData(request: any) {
		return companyManufacturerApi.getCompanyManufacturerGrid(request).then((response) => {
			return response.data;
		});
	}

	async function handleSubmitMake(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		if (dataManufacturers.length == 0) return;
		if (!guidCompany) return;

		const resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		let filesRequest = undefined;
		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		const request: CompanyManufacturerSaveRequest = {
			companyGuid: guidCompany,
			vehicleManufactures: dataManufacturers.map((item) => String(item.key)),
			changeJustification: {
				guidReason: resultConfirm.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm.observation || null,
				filesUpload: filesRequest,
			},
		};

		setIsLoadingAddManufacturers(true);

		companyManufacturerApi
			.saveCompanyManufacturers(request)
			.then(() => {
				toast.success(getTranslateByKey("information.valuatorManufacturerSavedSuccesfully"));
			})
			.finally(() => {
				setDataGrid([]);
				setDataManufacturers([]);
				resetFormFilters(guidCompany);
				setIsLoadingAddManufacturers(false);
			});
	}

	function resetFormFilters(companyGuid: string) {
		const initialFilters: CompanyManufacturerRequest = {
			companyGuid,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize10,
		};

		setFormFilters(initialFilters);
	}

	function handleLocalRemove(guidManufacturer: string) {
		if (!guidCompany) return;

		setDataGrid((prevGrid) => prevGrid.filter((manufacturer) => manufacturer.guid != guidManufacturer));

		const newTotalRecords = totalRecords - 1;
		const currentTotalPages = Math.ceil(totalRecords / (formFilters?.pageSize || PaginationEnum.PageSize10));
		const newTotalPages = Math.ceil(newTotalRecords / (formFilters?.pageSize || PaginationEnum.PageSize10));

		if (
			newTotalPages < currentTotalPages ||
			(dataGrid.length < PaginationEnum.PageSize10 && newTotalRecords > PaginationEnum.PageSize10)
		) {
			setTotalRecords(newTotalRecords);
			setDataGrid([]);
			resetFormFilters(guidCompany);
		} else {
			setTotalRecords(newTotalRecords);
		}
	}

	function handleChangeFilter(newFilter: string) {
		if (newFilter === formFilters?.searchTerm) return;
		const request: CompanyManufacturerRequest = {
			companyGuid: formFilters?.companyGuid ?? null,
			searchTerm: newFilter,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize10,
		};

		setDataGrid([]);
		setFormFilters(request);
	}
	// #endregion

	// #region UseEffects
	useEffect(() => {
		callManufacturerApiDomain();
	}, []);

	useEffect(() => {
		if (!guidCompany) return;

		resetFormFilters(guidCompany);

		setDataGrid([]);
	}, [guidCompany]);
	// #endregion

	const rowHeaders = [getTranslateByKey("information.manufacturer"), getTranslateByKey("information.actions")];

	const ID_SCROLLABLE_TARGET = "id-scrollable-companyManufacturer-container-manufacturers";

	return (
		<S.ContainerSection>
			<SP.HeaderSection>
				<SP.HeaderText>{getTranslateByKey("information.manufacturer")}</SP.HeaderText>
			</SP.HeaderSection>

			<form onSubmit={handleSubmitMake} noValidate>
				<S.RowSelect>
					<S.AutoCompleteField>
						<KOGAutoCompleteCheckbox
							label={getTranslateByKey("information.manufacturer")}
							isLoading={manufacturerReducer.isLoading}
							options={manufacturerReducer.data || []}
							valueCustom={dataManufacturers}
							selectAllLabel={getTranslateByKey("information.all")}
							setValueCustom={setDataManufacturers}
							customInputProps={{
								required: true,
								id: "id-companyManufacturer-select-manufacturer",
							}}
						/>
					</S.AutoCompleteField>

					<SecondaryButton
						isLoading={isLoadingAddManufacturers}
						type="submit"
						style={{
							padding: "0.2rem 0.8rem",
							margin: ".6rem 0 0",
						}}
						id="id-companyManufacturer-btn-addManufacturer"
					>
						{getTranslateByKey("information.addManufacturer")}
					</SecondaryButton>
					<S.AreaFilterGrid>
						<KogInputTextFilter
							id="id-filter-manufacturer-search"
							label={getTranslateByKey("information.typeToFilter")}
							onDebounce={handleChangeFilter}
						/>
					</S.AreaFilterGrid>
				</S.RowSelect>
			</form>

			<S.ContainerPagination id={ID_SCROLLABLE_TARGET}>
				<KOGPaginationGrid
					fetchEqualFilters={true}
					idScrollableTarget={ID_SCROLLABLE_TARGET}
					dataGrid={dataGrid}
					formFilters={formFilters}
					setDataGrid={setDataGrid}
					totalRecords={totalRecords}
					setTotalRecords={setTotalRecords}
					fetchMore={fetchData}
				>
					<BoxGrid rowHeaders={rowHeaders}>
						{dataGrid.map((companyManufacturer, _) => (
							<RowCompanyManufacturer
								key={companyManufacturer.guid}
								companyManufacturer={companyManufacturer}
								onRemove={handleLocalRemove}
							/>
						))}
					</BoxGrid>
				</KOGPaginationGrid>
			</S.ContainerPagination>
			<ModalConfirmationReason />
		</S.ContainerSection>
	);
}

export default CompanyManufacturer;
